import type { FC, ReactNode } from 'react';

import type { Props as ButtonProps } from '#src/atoms/buttons/Button/index.tsx';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { ConfirmDialog } from '#src/components/form/ConfirmDialog/index.tsx';
import { useToggle } from '#src/hooks/useToggle.ts';

type ConfirmationProps = {
  children: ReactNode;
  onConfirm: () => void;
  message?: ReactNode;
  fullScreen?: boolean;
  hideCancel?: boolean;
  disabled?: boolean;
  cta?: string;
  confirmCtaProps?: Omit<ButtonProps, 'size'> & { size?: never };
  disclaimer?: ReactNode;
};
type ButtonConfirmationProps = ConfirmationProps &
  Omit<ButtonProps, 'onClick' | 'children'> & {
    component?: FC<any>;
  };

export const ButtonBaseWithConfirmDialog = ({
  children,
  message,
  fullScreen,
  hideCancel,
  onConfirm,
  disabled,
  cta,
  confirmCtaProps,
  disclaimer,
}: ConfirmationProps) => {
  const [open, toggleOpen] = useToggle(false);

  return (
    <>
      <ButtonBase onClick={toggleOpen} disabled={disabled}>
        {children}
      </ButtonBase>
      <ConfirmDialog
        open={open}
        onClose={toggleOpen}
        cta={cta}
        message={message}
        onConfirm={onConfirm}
        fullScreen={fullScreen}
        hideCancel={hideCancel}
        ctaProps={confirmCtaProps}
        disclaimer={disclaimer}
      />
    </>
  );
};

export const ButtonWithConfirmDialog = (props: ButtonConfirmationProps) => {
  const {
    ref,
    children,
    message,
    fullScreen,
    hideCancel,
    onConfirm,
    disabled,
    cta,
    component: Component = Button,
    confirmCtaProps,
    disclaimer,
    ...rest
  } = props;

  const [open, toggleOpen] = useToggle(false);

  return (
    <>
      <Component onClick={toggleOpen} disabled={disabled} {...rest} ref={ref}>
        {children}
      </Component>
      <ConfirmDialog
        open={open}
        onClose={toggleOpen}
        cta={cta}
        message={message}
        onConfirm={onConfirm}
        fullScreen={fullScreen}
        hideCancel={hideCancel}
        ctaProps={confirmCtaProps}
        disclaimer={disclaimer}
      />
    </>
  );
};
